import {
  Box,
  Button,
  Dialog,
  Text,
  TextField,
  Tooltip,
} from '@radix-ui/themes';
import { Pen } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BankResponse, CreateBankRequest } from '../../model/Bank.model';
import bankService from '../../service/bank-service/Bank.service';

interface EditBankModalProps {
  bank: BankResponse;
  onSuccess: () => void;
}

const EditBankModal: React.FC<EditBankModalProps> = ({ bank, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<CreateBankRequest>({
    name: bank.name,
    address: bank.address,
    phoneNumber: bank.phoneNumber,
    email: bank.email || '',
    website: bank.website || '',
  });

  useEffect(() => {
    setFormData({
      name: bank.name,
      address: bank.address,
      phoneNumber: bank.phoneNumber,
      email: bank.email || '',
      website: bank.website || '',
    });
  }, [bank]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await bankService.editBank(bank.id, formData);
      toast.success('Cập nhật ngân hàng thành công!');
      onSuccess();
      setOpen(false);
    } catch (error: any) {
      console.error('Error updating bank:', error);
      toast.error(error.message || 'Có lỗi xảy ra khi cập nhật ngân hàng!');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>
        <Button variant="ghost" size="1" className="cursor-pointer">
          <Tooltip content="Edit">
            <Pen size={18} />
          </Tooltip>
        </Button>
      </Dialog.Trigger>

      <Dialog.Content style={{ maxWidth: 500 }}>
        <Dialog.Title>Chỉnh sửa</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Cập nhật thông tin ngân hàng
        </Dialog.Description>

        <form onSubmit={handleSubmit}>
          <Box className="space-y-4">
            <div>
              <Text as="label" size="2" mb="1" weight="bold">
                Tên ngân hàng <span className="text-red-500">*</span>
              </Text>
              <TextField.Root
                name="name"
                placeholder="Nhập tên ngân hàng"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <Text as="label" size="2" mb="1" weight="bold">
                Địa chỉ
              </Text>
              <TextField.Root
                name="address"
                placeholder="Nhập địa chỉ"
                value={formData.address}
                onChange={handleChange}
              />
            </div>

            <div>
              <Text as="label" size="2" mb="1" weight="bold">
                Số điện thoại
              </Text>
              <TextField.Root
                name="phoneNumber"
                placeholder="Nhập số điện thoại"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
            </div>

            <div>
              <Text as="label" size="2" mb="1" weight="bold">
                Email
              </Text>
              <TextField.Root
                type="email"
                name="email"
                placeholder="Nhập email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <Text as="label" size="2" mb="1" weight="bold">
                Website
              </Text>
              <TextField.Root
                name="website"
                placeholder="Nhập website"
                value={formData.website}
                onChange={handleChange}
              />
            </div>
          </Box>

          <div className="flex justify-end space-x-2 pt-4">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Hủy
              </Button>
            </Dialog.Close>
            <Button color="blue" variant="soft" type="submit">
              Hoàn thành
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default EditBankModal;
